import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  TrendingUp,
  ShoppingCart,
  Briefcase,
  Building2,
  GraduationCap,
  Scale,
  Hammer,
  Heart,
  UtensilsCrossed,
  ArrowRight,
  DollarSign,
  Bot,
  Users,
  Zap,
  AlertCircle,
  CheckCircle2,
  Plus,
  Minus,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { publicApi, stackApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { mergeGuestStackTools, parseGuestStack, saveGuestStack } from '../utils/runningStackStore';
import { buildBusinessContextNotice, loadBusinessContext, saveBusinessContext } from '../utils/businessContext';
import {
  BUSINESS_TYPES,
  normalizeBusinessType,
  resolveBusinessTypeFromStackId,
  DEFAULT_BUSINESS_TYPE,
  resolveStackIdFromBusinessType,
} from '../utils/businessTaxonomy';
import { extractStepCandidateGroups, resolveWorkflowTools } from '../utils/runningStackMatching';

// Unsplash image URLs for business-type backgrounds
export const STACK_PRESCRIPTIONS = [
  {
    id: 'real-estate',
    title: 'Real Estate Agency',
    icon: Home,
    color: '#FF6B6B',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
    painPoints: [
      'Manual CRM data entry for leads',
      'Time-consuming property description writing',
      'No automated follow-up sequences',
      'Slow comparative market analysis',
    ],
    toolStack: [
      { name: 'Zapier', role: 'Form-to-CRM automation', order: 1 },
      { name: 'Copy.ai or Claude', role: 'Property listing copy generation', order: 2 },
      { name: 'Make.com', role: 'Lead nurture workflows', order: 3 },
      { name: 'Airtable + ChatGPT', role: 'Market data analysis & summaries', order: 4 },
    ],
    agentInsertIndex: 1,
    agentRole: 'Qualifies inbound leads, routes hot buyers to agents, and schedules follow-up tasks automatically',
    implementationWindow: '7-10 days',
    outcome: 'Save 8-12 hrs/week on admin, generate 40% more personalized follow-ups, close listings 20% faster',
    annualCost: '$3,200',
    costBreakdown: 'Zapier $50, Copy.ai $40, Make $200, Airtable+Claude $100/mo',
    competitorNote:
      'Unlike generic AI tools, this stack shows the *sequence* - form -> CRM -> copy -> nurture. Every tool has a job.',
    metric: '8-12 hrs/week saved',
  },
  {
    id: 'marketing-agency',
    title: 'Marketing Agency',
    icon: TrendingUp,
    color: '#4ECDC4',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop',
    painPoints: [
      'Manual content calendar planning',
      'Slow A/B test iteration cycles',
      'Repetitive performance report generation',
      'No real-time ad-copy optimization',
    ],
    toolStack: [
      { name: 'Content Planner (e.g., Notion AI)', role: 'Monthly content strategy', order: 1 },
      { name: 'Jasper or Copy.ai', role: 'Bulk ad-copy variants for testing', order: 2 },
      { name: 'Meta Ads API + Zapier', role: 'Auto-launch A/B tests', order: 3 },
      { name: 'Data Studio + Claude', role: 'Instant performance narratives', order: 4 },
      { name: 'Loom', role: 'Client-ready async performance walkthroughs', order: 5 },
    ],
    agentInsertIndex: 3,
    agentRole: 'Monitors campaign deltas, triggers ad refresh tasks, and drafts weekly client summaries',
    implementationWindow: '10-14 days',
    outcome: 'Deliver reports 5x faster, launch campaigns 3 days sooner, improve ROAS by 15-25%',
    annualCost: '$4,800',
    costBreakdown: 'Notion AI $10, Jasper $125, Zapier+Meta API $300, Data Studio+Claude $150/mo',
    competitorNote:
      'Agencies use our recommendations as a *consulting template*; clients see the ROI math upfront.',
    metric: '5x faster reporting',
  },
  {
    id: 'ecommerce',
    title: 'E-commerce & Retail SMB',
    icon: ShoppingCart,
    color: '#95E1D3',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=600&fit=crop',
    painPoints: [
      'Manual customer service escalations',
      'Slow product description generation',
      'No dynamic ad-copy personalization',
      'Inventory-to-reorder manually tracked',
    ],
    toolStack: [
      { name: 'Intercom + Claude or ChatGPT', role: 'AI customer support tier 1', order: 1 },
      { name: 'Shopify AI or Describely', role: 'Product description bulk generation', order: 2 },
      { name: 'Klaviyo + Zapier', role: 'Personalized email campaigns (dynamic content)', order: 3 },
      { name: 'Inventory API + Make', role: 'Low-stock alerts -> reorder workflows', order: 4 },
    ],
    agentInsertIndex: 0,
    agentRole: 'Classifies support tickets, escalates urgent cases, and keeps order + CRM data in sync',
    implementationWindow: '7-12 days',
    outcome: 'Reduce support response time by 70%, upload 100+ products/week, boost email revenue 40%',
    annualCost: '$2,800',
    costBreakdown: 'Intercom $99, Describely $99, Klaviyo $20, Make $99, APIs free/native/mo',
    competitorNote:
      'SMBs see the *before-after* for each pain; not just a tool list, but a playbook.',
    metric: '100+ products/week',
  },
  {
    id: 'accounting',
    title: 'Accounting & Finance Professional',
    icon: Briefcase,
    color: '#FFB4A2',
    image: 'https://images.unsplash.com/photo-1507425886519-51a028ceb869?w=1200&h=600&fit=crop',
    painPoints: [
      'Manual document review & extraction',
      'Slow client tax summary generation',
      'Duplicate invoice/expense data entry',
      'No proactive financial health alerts',
    ],
    toolStack: [
      { name: 'Document AI or Nvidia NIM', role: 'Receipt & invoice OCR/extraction', order: 1 },
      { name: 'Claude Sonnet', role: 'Tax summary & compliance narrative', order: 2 },
      { name: 'Zapier + Stripe/QB API', role: 'Transaction auto-categorization', order: 3 },
      { name: 'Retool + Alert Bot', role: 'Client dashboard with anomaly alerts', order: 4 },
      { name: 'Google Drive + Rules Engine', role: 'Client document collection and deadline tracking', order: 5 },
    ],
    agentInsertIndex: 2,
    agentRole: 'Flags risky transactions, drafts client follow-up requests, and checks filing readiness',
    implementationWindow: '12-18 days',
    outcome: 'Cut document processing 80%, deliver tax packages 2 weeks early, retain 95% of clients',
    annualCost: '$3,600',
    costBreakdown: 'Document AI $100-200, Claude API $4-20, Zapier $50, Retool $50/mo',
    competitorNote:
      'Positioned as a *competitive advantage*; accountants pitch this stack to win clients.',
    metric: '80% faster processing',
  },
  {
    id: 'healthcare',
    title: 'Healthcare Practice',
    icon: Heart,
    color: '#FDA7DF',
    image: 'https://images.unsplash.com/photo-1576091160693-86c3c9d0cef0?w=1200&h=600&fit=crop',
    painPoints: [
      'Manual appointment reminders & rescheduling',
      'No patient intake form automation',
      'Slow follow-up communication',
      'Tedious after-visit note summarization',
    ],
    toolStack: [
      { name: 'Twilio + Make', role: 'SMS appointment reminders + no-show prevention', order: 1 },
      { name: 'Forms + Claude', role: 'AI intake form pre-population & risk flagging', order: 2 },
      { name: 'HubSpot CRM + Zapier', role: 'Patient follow-up sequences', order: 3 },
      { name: 'Note-taking API + Sonnet', role: 'Clinical note auto-summarization (HIPAA-safe)', order: 4 },
    ],
    agentInsertIndex: 2,
    agentRole: 'Enforces triage rules, routes follow-ups by urgency, and audits missed patient tasks',
    implementationWindow: '14-21 days',
    outcome: 'Reduce no-shows by 30%, cut admin time 6 hrs/week, improve patient satisfaction 22%',
    annualCost: '$2,400',
    costBreakdown: 'Twilio $50, Make $99, HubSpot $50, Zapier $30, Claude API $20/mo (HIPAA tier)',
    competitorNote:
      'Shown as a *conversion funnel*: pain -> tool sequence -> $$ saved. Clinics see instant ROI.',
    metric: '30% fewer no-shows',
  },
  {
    id: 'restaurant',
    title: 'Restaurant & Hospitality',
    icon: UtensilsCrossed,
    color: '#F4A261',
    image: 'https://images.unsplash.com/photo-1577720643272-265f434b3c8f?w=1200&h=600&fit=crop',
    painPoints: [
      'Manual menu writing & seasonal updates',
      'No automated review response system',
      'Slow reservation & seating optimization',
      'Repetitive reservation confirmations',
    ],
    toolStack: [
      { name: 'Menu AI (Plate or ChefGPT)', role: 'Dynamic menu generation & optimization', order: 1 },
      { name: 'ReviewBot + Claude', role: 'Auto-response to Yelp/Google reviews (brand-safe)', order: 2 },
      { name: 'Zapier + Resy API', role: 'Reservation automation & table optimization', order: 3 },
      { name: 'Twilio + Make', role: 'SMS confirmations & no-show reduction', order: 4 },
    ],
    agentInsertIndex: 1,
    agentRole: 'Balances seating load, triggers reminder campaigns, and escalates negative review patterns',
    implementationWindow: '6-9 days',
    outcome: 'Reduce no-shows 25%, respond to 100% of reviews in <2hrs, cut ops admin 4 hrs/week',
    annualCost: '$2,200',
    costBreakdown: 'Menu AI $40, ReviewBot $50, Zapier+Resy $60, Twilio+Make $50/mo',
    competitorNote: 'Multi-location chains scale this across venues without hiring more ops staff.',
    metric: '25% fewer no-shows',
  },
  {
    id: 'legal-practice',
    title: 'Law Firm & Legal Ops',
    icon: Scale,
    color: '#7BDFF2',
    image: 'https://images.unsplash.com/photo-1453945619913-79ec89a82c51?w=1200&h=600&fit=crop',
    painPoints: [
      'Slow contract review cycles',
      'Manual clause comparison across versions',
      'Inconsistent client update communication',
      'Deadline tracking spread across inboxes',
    ],
    toolStack: [
      { name: 'Spellbook / Harvey', role: 'Contract drafting and redline suggestions', order: 1 },
      { name: 'Clause Library + Notion', role: 'Approved clause retrieval and playbook governance', order: 2 },
      { name: 'Docketing + Zapier', role: 'Court/calendar deadline synchronization', order: 3 },
    ],
    agentInsertIndex: 2,
    agentRole: 'Checks clause risk, tracks due dates, and creates client-ready status digests',
    implementationWindow: '9-14 days',
    outcome: 'Reduce first-pass review time by 45%, cut deadline misses, and improve client transparency',
    annualCost: '$3,900',
    costBreakdown: 'Spellbook $89, Notion $18, Zapier $50, docketing integration $150/mo',
    competitorNote: 'Designed to improve legal throughput without sacrificing auditability or quality controls.',
    metric: '45% faster review',
  },
  {
    id: 'education-coaching',
    title: 'Education & Coaching',
    icon: GraduationCap,
    color: '#B8F2E6',
    image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200&h=600&fit=crop',
    painPoints: [
      'Time spent creating learning materials',
      'Inconsistent student follow-up',
      'Manual feedback summarization',
      'Low learner completion rates',
    ],
    toolStack: [
      { name: 'ChatGPT Team', role: 'Curriculum draft and lesson variants', order: 1 },
      { name: 'LMS + Make', role: 'Progress-triggered reminders and nudges', order: 2 },
      { name: 'Typeform + Sheets', role: 'Assessment capture and learner profiling', order: 3 },
      { name: 'Loom', role: 'Personalized async feedback videos', order: 4 },
      { name: 'Canva Magic Studio', role: 'Visual worksheet and handout generation', order: 5 },
    ],
    agentInsertIndex: 1,
    agentRole: 'Creates weekly intervention lists and suggests personalized next lessons per learner',
    implementationWindow: '8-12 days',
    outcome: 'Produce content 3x faster and improve learner completion by 20-30%',
    annualCost: '$2,600',
    costBreakdown: 'ChatGPT Team $25/user, Make $99, Typeform $29, Loom $12.5, Canva $15/mo',
    competitorNote: 'Built for solo educators and cohort-based programs needing repeatable, personalized delivery.',
    metric: '20-30% higher completion',
  },
  {
    id: 'construction-trades',
    title: 'Construction & Field Services',
    icon: Hammer,
    color: '#F7B267',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&h=600&fit=crop',
    painPoints: [
      'Job updates spread across calls and texts',
      'Manual quote generation from site notes',
      'Slow dispatch and scheduling changes',
      'Missed material reorder timing',
    ],
    toolStack: [
      { name: 'Jobber / ServiceTitan', role: 'Work order and dispatch hub', order: 1 },
      { name: 'Voice Notes + Whisper', role: 'Convert site notes to structured tasks', order: 2 },
      { name: 'Zapier + QuickBooks', role: 'Quote-to-invoice sync', order: 3 },
      { name: 'Inventory + Alerts', role: 'Material threshold and reorder trigger', order: 4 },
    ],
    agentInsertIndex: 3,
    agentRole: 'Detects schedule conflicts, prepares ETA updates, and queues quote drafts for approval',
    implementationWindow: '10-16 days',
    outcome: 'Cut admin overhead by 30% and reduce schedule disruptions across active jobs',
    annualCost: '$3,300',
    costBreakdown: 'Service hub $129, Whisper/API $20, Zapier $50, accounting/inventory add-ons $75/mo',
    competitorNote: 'Optimized for field realities: messy inputs, frequent changes, and low tolerance for delays.',
    metric: '30% less admin time',
  },
  {
    id: 'saas-startup',
    title: 'SaaS Startup Team',
    icon: Building2,
    color: '#A0C4FF',
    image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1200&h=600&fit=crop',
    painPoints: [
      'Support, product, and sales data in silos',
      'Slow release-note and onboarding content creation',
      'Manual churn-risk tracking',
      'Inconsistent handoff from trial to paid',
    ],
    toolStack: [
      { name: 'HubSpot + Product Analytics', role: 'Unified customer lifecycle signals', order: 1 },
      { name: 'Intercom Fin + Docs AI', role: 'Self-serve support + help center automation', order: 2 },
      { name: 'Notion + Claude', role: 'Release notes, onboarding copy, and changelog drafts', order: 3 },
      { name: 'Webhook Orchestrator', role: 'Usage-triggered lifecycle campaigns', order: 4 },
      { name: 'BI Dashboard', role: 'ARR, activation, and churn risk visibility', order: 5 },
      { name: 'Experiment Tracker', role: 'A/B experimentation record and rollout guardrails', order: 6 },
    ],
    agentInsertIndex: 4,
    agentRole: 'Scores churn risk weekly, assigns recovery playbooks, and flags onboarding bottlenecks',
    implementationWindow: '14-24 days',
    outcome: 'Improve activation, reduce churn signals earlier, and align GTM + product execution',
    annualCost: '$5,400',
    costBreakdown: 'HubSpot suite $800, Intercom $299, Claude $50, orchestration + BI tools $300/mo',
    competitorNote: 'Balances growth speed with operational discipline so teams scale without stack chaos.',
    metric: 'Faster activation cycles',
  },
  {
    id: 'public-services-office',
    title: 'Public Services Office',
    icon: Briefcase,
    color: '#8EC5FC',
    image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&h=600&fit=crop',
    painPoints: [
      'Service requests are triaged manually across channels',
      'Response templates are inconsistent by team',
      'Backlog reporting takes days to assemble',
      'Escalations lack clear SLA tracking',
    ],
    toolStack: [
      { name: 'Microsoft Forms + Power Automate', role: 'Standardized citizen intake and routing', order: 1 },
      { name: 'Copilot Studio', role: 'Self-serve request assistant for common issues', order: 2 },
      { name: 'Dynamics 365', role: 'Case lifecycle and SLA workflow management', order: 3 },
      { name: 'Power BI', role: 'Backlog, SLA, and resolution trend dashboard', order: 4 },
    ],
    agentInsertIndex: 1,
    agentRole: 'Classifies requests, prioritizes urgent cases, and drafts compliant responses for review',
    implementationWindow: '14-21 days',
    outcome: 'Reduce response delays and improve service consistency across public-facing teams',
    annualCost: '$4,200',
    costBreakdown: 'Power Platform + Copilot licenses and BI capacity for multi-team operations',
    competitorNote: 'Built for accountability-heavy environments where SLA traceability is mandatory.',
    metric: '35% faster request handling',
  },
  {
    id: 'nonprofit-fundraising-team',
    title: 'Nonprofit Fundraising Team',
    icon: Heart,
    color: '#F9A8D4',
    image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&h=600&fit=crop',
    painPoints: [
      'Donor segmentation is manually maintained in spreadsheets',
      'Grant update emails are repetitive and delayed',
      'Volunteer outreach cadence is inconsistent',
      'Campaign performance reports are assembled by hand',
    ],
    toolStack: [
      { name: 'HubSpot CRM', role: 'Donor and volunteer lifecycle management', order: 1 },
      { name: 'Mailchimp + Zapier', role: 'Automated segment-based campaign sequences', order: 2 },
      { name: 'ChatGPT Team', role: 'Grant update, appeal letter, and recap drafting', order: 3 },
      { name: 'Looker Studio', role: 'Real-time donor funnel and campaign ROI visibility', order: 4 },
    ],
    agentInsertIndex: 2,
    agentRole: 'Prioritizes lapsed donor outreach and drafts personalized re-engagement plans weekly',
    implementationWindow: '10-15 days',
    outcome: 'Increase donor retention and keep campaign communication consistent across teams',
    annualCost: '$2,900',
    costBreakdown: 'CRM, email automation, AI drafting, and reporting stack for nonprofit scale',
    competitorNote: 'Focused on lean teams that need predictable fundraising operations without extra headcount.',
    metric: '22% higher donor retention',
  },
  {
    id: 'winter-home-services',
    title: 'Winter Home Services',
    icon: Hammer,
    color: '#60A5FA',
    image: 'https://images.unsplash.com/photo-1489515217757-5fd1be406fef?w=1200&h=600&fit=crop',
    painPoints: [
      'Snow and emergency calls create dispatch bottlenecks',
      'Route changes are communicated manually',
      'Service notes are incomplete or unstructured',
      'Invoicing lags behind completed jobs',
    ],
    toolStack: [
      { name: 'Jobber', role: 'Dispatch, scheduling, and technician status hub', order: 1 },
      { name: 'Google Maps + Route Optimization', role: 'Dynamic route sequencing for crews', order: 2 },
      { name: 'Whisper API', role: 'Voice-to-work-order notes from field teams', order: 3 },
      { name: 'QuickBooks + Zapier', role: 'Service completion to invoice automation', order: 4 },
    ],
    agentInsertIndex: 2,
    agentRole: 'Predicts schedule conflicts from weather events and triggers customer ETA notifications',
    implementationWindow: '8-12 days',
    outcome: 'Keep crews on schedule during peak winter demand and reduce invoice delays',
    annualCost: '$3,100',
    costBreakdown: 'Field service platform, route tools, transcription API, and accounting automation',
    competitorNote: 'Purpose-built for weather-driven operations where timing and communication are critical.',
    metric: '31% fewer dispatch delays',
  },
  {
    id: 'manufacturing-smb',
    title: 'Manufacturing SMB',
    icon: Building2,
    color: '#A7F3D0',
    image: 'https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=1200&h=600&fit=crop',
    painPoints: [
      'Production updates rely on manual shift handoffs',
      'Maintenance requests are not prioritized consistently',
      'Supplier reorder timing is reactive',
      'Leadership reporting lags operational reality',
    ],
    toolStack: [
      { name: 'Airtable', role: 'Production board and issue tracking', order: 1 },
      { name: 'Make.com', role: 'Shift-to-shift update automation and alerting', order: 2 },
      { name: 'Power BI', role: 'Line performance and downtime visibility', order: 3 },
      { name: 'Inventory Alerts', role: 'Threshold-based reorder and supplier notification', order: 4 },
    ],
    agentInsertIndex: 2,
    agentRole: 'Detects recurring downtime patterns and schedules preventive action recommendations',
    implementationWindow: '15-24 days',
    outcome: 'Improve uptime, streamline handoffs, and reduce reactive procurement decisions',
    annualCost: '$4,600',
    costBreakdown: 'Workflow orchestration, analytics, and inventory intelligence stack for operations',
    competitorNote: 'Creates one operational rhythm from floor updates to leadership decisions.',
    metric: '18% downtime reduction',
  },
];

const BUSINESS_TYPE_RIBBON_ITEMS = BUSINESS_TYPES.map((item) => ({
  id: item.id,
  title: item.label,
  stackId: resolveStackIdFromBusinessType(item.id, 'legal-practice'),
}));

const INDUSTRY_ICON_OVERRIDES = {
  legal: Scale,
  accounting: Briefcase,
  real_estate: Home,
  dental: Heart,
  medical_clinics: Heart,
  hvac: Hammer,
  plumber: Hammer,
  electrician: Zap,
  law_enforcement_supplier: Building2,
  immigration_consultant: Users,
  logistics: Hammer,
  ecommerce: ShoppingCart,
  hr_recruiting: Users,
  non_profits: Heart,
  construction: Hammer,
  hospitality: UtensilsCrossed,
  insurance: Briefcase,
  wealth_management: Briefcase,
  creative_agency: TrendingUp,
  hvac_solar: Hammer,
  trade_schools: GraduationCap,
  it_msps: Building2,
  small_farms: Home,
  print_publishing: TrendingUp,
  labs_diagnostics: Heart,
};

const stackIdSetFromItems = (items = []) => new Set(
  (items || [])
    .map((item) => item?.tool_id || item?.id || item?.toolId || '')
    .filter(Boolean)
);

const AIStackPrescriptionsPage = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const isLightTheme = theme === 'light';
  const [selectedStack, setSelectedStack] = useState(STACK_PRESCRIPTIONS[0]);
  const [selectedBusinessType, setSelectedBusinessType] = useState(DEFAULT_BUSINESS_TYPE);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [allToolsCatalog, setAllToolsCatalog] = useState([]);
  const [stackActionMessage, setStackActionMessage] = useState('');
  const [addingPreset, setAddingPreset] = useState(false);

  const pageShellClass = isLightTheme ? 'bg-[#F6F8FB] text-[#0F172A]' : 'bg-[#050505] text-white';
  const heroTitleClass = isLightTheme ? 'text-[#0F172A]' : 'text-white';
  const bodyTextClass = isLightTheme ? 'text-[#475569]' : 'text-[#A0A0A5]';
  const mutedTextClass = isLightTheme ? 'text-[#64748B]' : 'text-[#66666E]';
  const heroCardClass = isLightTheme
    ? 'mb-3 px-4 py-3 border border-[#D8E0EA] bg-white/90 shadow-[0_10px_30px_rgba(15,23,42,0.08)]'
    : 'mb-3 px-4 py-3 border border-[rgba(255,232,0,0.45)] bg-[rgba(255,232,0,0.08)] shadow-[0_0_28px_rgba(255,232,0,0.20)]';
  const heroStatCardClass = isLightTheme
    ? 'terminal-grid p-3 border border-[#D8E0EA] bg-white/90'
    : 'terminal-grid p-3 border border-white/10';
  const heroRibbonClass = isLightTheme
    ? 'mt-5 border-y border-[#D8E0EA] py-3 overflow-hidden bg-white/75 backdrop-blur-sm'
    : 'mt-5 border-y border-white/10 py-3 overflow-hidden bg-[#0B0C10]/45 backdrop-blur-sm';
  const selectedIndustryClass = isLightTheme
    ? 'industry-glow border-[#B67A00] bg-white text-[#0F172A]'
    : 'industry-glow border-[#FFE800] bg-[#181818]';
  const insightPanelClass = isLightTheme
    ? 'terminal-grid p-5 border border-[#BFDDF2] bg-[#F4FAFF]/90 mb-8'
    : 'terminal-grid p-5 border border-[#00F0FF]/30 bg-[#00F0FF]/[0.04] mb-8';
  const whyPanelClass = isLightTheme
    ? 'terminal-grid p-5 border border-[#CDE6CF] bg-[#F4FFF7]/90 mb-8'
    : 'terminal-grid p-5 border border-[#39FF14]/30 bg-[#39FF14]/5 mb-8';
  const darkInfoBoxClass = isLightTheme
    ? 'bg-white border border-[#D8E0EA] p-3 rounded-none'
    : 'bg-[#15161A] border border-white/5 p-3 rounded-none';
  const stageCardClass = isLightTheme
    ? 'border border-[#D8E0EA] p-3 bg-white/90'
    : 'border border-white/10 p-3 bg-black/20';
  const tableSectionClass = isLightTheme
    ? 'bg-[#F6F8FB] border-y border-[#D8E0EA] py-16 px-6'
    : 'bg-[#0B0C10] border-y border-white/10 py-16 px-6';
  const nextStepsSectionClass = isLightTheme
    ? 'bg-[#F6F8FB] border-t border-[#D8E0EA] py-16 px-6'
    : 'bg-[#0B0C10] border-t border-white/10 py-16 px-6';

  const persistBusinessSelection = useCallback((stackId, businessTypeOverride = '') => {
    const normalizedBusinessType = normalizeBusinessType(
      businessTypeOverride || resolveBusinessTypeFromStackId(stackId, DEFAULT_BUSINESS_TYPE),
      DEFAULT_BUSINESS_TYPE,
    );
    saveBusinessContext({
      selectedStackId: stackId || resolveStackIdFromBusinessType(normalizedBusinessType, ''),
      businessType: normalizedBusinessType,
      sourceScreen: 'ai-stacks',
    }, isAuthenticated);
  }, [isAuthenticated]);

  const whyWowrollaFaqs = [
    {
      question: 'Why Wowrolla instead of Futurepedia or basic AI directories?',
      answer:
        'Directories tell you what exists. Wowrolla prescribes what to combine, in what order, and why it solves your exact operational bottleneck.',
    },
    {
      question: 'What is the advantage of adding AI agents to these stacks?',
      answer:
        'AI agents orchestrate tools across steps: they route tasks, trigger automations, enforce rules, and close loops between CRM, messaging, analytics, and content systems.',
    },
    {
      question: 'How is this better than picking one "best" AI tool?',
      answer:
        'One tool cannot fix fragmented workflows. The advantage comes from connected systems where each tool handles a stage and the AI agent coordinates the entire journey.',
    },
    {
      question: 'How does Wowrolla keep recommendations practical for SMB teams?',
      answer:
        'Each stack includes realistic annual cost, implementation sequence, and measurable outcomes, so teams can execute in weeks instead of experimenting blindly for months.',
    },
  ];

  const workflowWithAgent = useMemo(() => {
    const base = selectedStack.toolStack || [];
    const insertAt = Math.min(selectedStack.agentInsertIndex ?? Math.ceil(base.length / 2), base.length);
    const agentStep = {
      name: 'Wowrolla AI Agent',
      role:
        selectedStack.agentRole ||
        'Orchestrates handoffs across tools, enforces business rules, and triggers next-step automations',
      isAgent: true,
    };

    return [...base.slice(0, insertAt), agentStep, ...base.slice(insertAt)];
  }, [selectedStack]);

  const tableToolCount = useMemo(() => {
    return Object.fromEntries(
      STACK_PRESCRIPTIONS.map((stack) => [stack.id, (stack.toolStack?.length || 0) + 1])
    );
  }, []);

  const technicalWorkflow = useMemo(() => {
    const isAiTool = (toolName = '') => /ai|gpt|claude|sonnet|bot|copilot|harvey|spellbook|whisper|fin|chat|make|zapier|automate|automation|integromat|workato|n8n/i.test(toolName);
    const intakeSteps = workflowWithAgent.slice(0, 2);
    const coreSteps = workflowWithAgent.slice(2, Math.max(3, workflowWithAgent.length - 1));
    const deliverySteps = workflowWithAgent.slice(Math.max(3, workflowWithAgent.length - 1));

    const splitTools = (steps) => {
      const ai = [];
      const nonAi = [];
      steps.forEach((step) => {
        if (step.isAgent || isAiTool(step.name)) ai.push(step.name);
        else nonAi.push(step.name);
      });
      return { ai, nonAi };
    };

    const intake = splitTools(intakeSteps);
    const core = splitTools(coreSteps);
    const delivery = splitTools(deliverySteps);

    return [
      {
        stage: '1) Intake + Signal Capture',
        objective: 'Collect normalized business signals and eliminate raw-input chaos',
        aiTools: intake.ai,
        nonAiTools: intake.nonAi,
        integrations: ['Forms/Webhooks', 'CRM fields sync', 'Validation rules'],
        agentAction: 'Validates incoming data, deduplicates records, and routes urgent intents',
      },
      {
        stage: '2) Decision + Orchestration Layer',
        objective: 'Apply decision logic and coordinate workflow transitions',
        aiTools: core.ai,
        nonAiTools: core.nonAi,
        integrations: ['Automation router', 'Conditional branching', 'Retry + fallback policy'],
        agentAction: selectedStack.agentRole,
      },
      {
        stage: '3) Delivery + Feedback Loop',
        objective: 'Ship output, track outcomes, and feed performance back into the next cycle',
        aiTools: delivery.ai,
        nonAiTools: delivery.nonAi,
        integrations: ['Dashboard/BI sync', 'Notification layer', 'SLA monitoring'],
        agentAction: 'Compiles execution telemetry, surfaces anomalies, and recommends next optimization pass',
      },
    ];
  }, [workflowWithAgent, selectedStack]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stackFromQuery = params.get('stack');
    const storedContext = loadBusinessContext(isAuthenticated);
    const stackFromStorage = storedContext.selectedStackId;
    const initialStackId = stackFromQuery || stackFromStorage;
    const initialBusinessType = normalizeBusinessType(
      storedContext.businessType || resolveBusinessTypeFromStackId(initialStackId, DEFAULT_BUSINESS_TYPE),
      DEFAULT_BUSINESS_TYPE,
    );
    if (initialStackId) {
      const match = STACK_PRESCRIPTIONS.find((stack) => stack.id === initialStackId);
      if (match) {
        setSelectedStack(match);
        setSelectedBusinessType(initialBusinessType);
        persistBusinessSelection(match.id, initialBusinessType);
      }
    }
  }, [isAuthenticated, location.search, persistBusinessSelection]);

  useEffect(() => {
    if (selectedStack?.id) {
      persistBusinessSelection(selectedStack.id, selectedBusinessType);
    }
  }, [persistBusinessSelection, selectedBusinessType, selectedStack]);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const response = await publicApi.getAllTools(false);
        setAllToolsCatalog(response.data || []);
      } catch {
        setAllToolsCatalog([]);
      }
    };
    loadCatalog();
  }, []);

  const carouselStacks = useMemo(() => {
    const stackById = new Map(STACK_PRESCRIPTIONS.map((stack) => [stack.id, stack]));

    const resolved = BUSINESS_TYPE_RIBBON_ITEMS.map((businessType) => {
      const mappedStack = stackById.get(businessType.stackId) || STACK_PRESCRIPTIONS[0];
      return {
        ...businessType,
        mappedStack,
        icon: INDUSTRY_ICON_OVERRIDES[businessType.id] || mappedStack.icon || Building2,
      };
    });

    return [...resolved, ...resolved];
  }, []);

  const minAnnualCost = useMemo(() => {
    const values = STACK_PRESCRIPTIONS
      .map((stack) => Number.parseInt(String(stack.annualCost || '').replace(/[^0-9]/g, ''), 10))
      .filter((value) => !Number.isNaN(value));
    return values.length ? `$${Math.min(...values).toLocaleString()}` : '$2,200';
  }, []);

  const maxAnnualCost = useMemo(() => {
    const values = STACK_PRESCRIPTIONS
      .map((stack) => Number.parseInt(String(stack.annualCost || '').replace(/[^0-9]/g, ''), 10))
      .filter((value) => !Number.isNaN(value));
    return values.length ? `$${Math.max(...values).toLocaleString()}` : '$5,400';
  }, []);

  const resolveStackToolCoverage = useCallback(() => {
    const workflowSteps = (workflowWithAgent || []).map((step) => ({ name: step?.name || '', isAgent: !!step?.isAgent }));
    const matchedTools = resolveWorkflowTools({
      toolStack: workflowSteps,
      allToolsCatalog,
    });

    const unresolvedSteps = workflowSteps
      .filter((step) => Boolean(step.name))
      .filter((step) => {
        const stepMatches = resolveWorkflowTools({
          toolStack: [{ name: step.name }],
          allToolsCatalog,
        });
        return stepMatches.length === 0;
      })
      .map((step) => step.name);

    const totalExpectedGroups = workflowSteps
      .flatMap((step) => extractStepCandidateGroups(step?.name || ''))
      .length;

    return {
      matchedTools,
      unresolvedSteps,
      totalExpectedGroups,
    };
  }, [allToolsCatalog, workflowWithAgent]);

  const buildGuestWorkflowAdditions = useCallback((matchedTools = [], unresolvedSteps = []) => {
    const matchedItems = (matchedTools || []).map((tool) => ({
      tool_id: tool.id,
      tool_slug: tool.slug,
      name: tool.name,
      slug: tool.slug,
      category_name: tool.category_name || '',
      monthly_cost: Number(tool.monthly_cost || 0),
      notes: '',
    }));

    const placeholderItems = (unresolvedSteps || []).map((name) => {
      const normalized = String(name || '')
        .toLowerCase()
        .replace(/\(.*?\)/g, ' ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50);
      const placeholderId = `workflow-${normalized || 'tool'}`;
      return {
        tool_id: placeholderId,
        tool_slug: placeholderId,
        name: name || 'Workflow tool',
        slug: placeholderId,
        category_name: 'Workflow tool',
        monthly_cost: 0,
        notes: 'Catalog match pending. Added from workflow step.',
      };
    });

    return [...matchedItems, ...placeholderItems];
  }, []);

  const addSelectedToolsToMyStack = useCallback(async () => {
    setAddingPreset(true);
    setStackActionMessage('');

    try {
      const { matchedTools, unresolvedSteps, totalExpectedGroups } = resolveStackToolCoverage();
      if (!matchedTools.length && (!unresolvedSteps || unresolvedSteps.length === 0)) {
        setStackActionMessage('Could not auto-match tools from this workflow yet. Use Find or Build to seed your stack.');
        return;
      }

      let addedCount = 0;
      if (isAuthenticated) {
        const beforeResponse = await stackApi.getDashboardStack();
        const beforeIds = stackIdSetFromItems(beforeResponse?.data?.items || []);

        const unresolvedPayloads = (unresolvedSteps || []).map((name) => {
          const normalized = String(name || '')
            .toLowerCase()
            .replace(/\(.*?\)/g, ' ')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 50);
          const placeholderId = `workflow-${normalized || 'tool'}`;
          return {
            tool_id: placeholderId,
            tool_slug: placeholderId,
            tool_name: name || 'Workflow tool',
            category_name: 'Workflow tool',
            notes: 'Catalog match pending. Added from workflow step.',
            monthly_cost: 0,
          };
        });

        const upsertPayloads = [
          ...(matchedTools || []).map((tool) => ({ tool_id: tool.id, tool_slug: tool.slug })),
          ...unresolvedPayloads,
        ];

        const settled = await Promise.allSettled(
          upsertPayloads.map((payload) => stackApi.addStackItem(payload))
        );

        // If every add fails, preserve the error path to avoid showing a false success message.
        if (!settled.some((result) => result.status === 'fulfilled')) {
          throw new Error('All add requests failed');
        }

        const afterResponse = await stackApi.getDashboardStack();
        const afterIds = stackIdSetFromItems(afterResponse?.data?.items || []);
        addedCount = Math.max(afterIds.size - beforeIds.size, 0);
      } else {
        const current = parseGuestStack();
        const beforeIds = stackIdSetFromItems(current);
        const additions = buildGuestWorkflowAdditions(matchedTools, unresolvedSteps);
        const next = mergeGuestStackTools(current, additions);
        saveGuestStack(next);
        const afterIds = stackIdSetFromItems(next);
        addedCount = Math.max(afterIds.size - beforeIds.size, 0);
      }

      if (!addedCount) {
        setStackActionMessage('No new workflow tools were added because these tools are already in your stack or could not be resolved from the catalog.');
        return;
      }

      const previousContext = loadBusinessContext(isAuthenticated);
      persistBusinessSelection(selectedStack.id);
      window.dispatchEvent(new Event('wowrolla:stack-updated'));
      const expectedMinimum = totalExpectedGroups;
      const unmatchedSteps = unresolvedSteps;

      if (addedCount < expectedMinimum || unmatchedSteps.length > 0) {
        const unresolvedPreview = unmatchedSteps.slice(0, 3).join(', ');
        const unresolvedSuffix = unresolvedPreview
          ? ` Unresolved steps for this business type: ${unresolvedPreview}${unmatchedSteps.length > 3 ? ', ...' : ''}.`
          : '';
        const matchedPrefix = `Added ${addedCount} workflow tools${unmatchedSteps.length ? ' (including unresolved workflow placeholders)' : ''}.`;
        setStackActionMessage(`${matchedPrefix}${unresolvedSuffix}`);
      } else {
        setStackActionMessage(buildBusinessContextNotice({ previousBusinessType: previousContext.businessType, nextStackLabel: selectedStack.title }) || `Added ${addedCount} workflow tools to your running stack.`);
      }
    } catch {
      setStackActionMessage('Could not add tools right now. Please try again.');
    } finally {
      setAddingPreset(false);
    }
  }, [buildGuestWorkflowAdditions, isAuthenticated, persistBusinessSelection, resolveStackToolCoverage, selectedStack]);

  return (
    <div className={`min-h-screen ${pageShellClass}`} data-testid="ai-stack-prescriptions-page">
      <SEOHead
        pageType="content"
        title="AI Stack Prescriptions: Workflow Templates for SMBs & Professionals"
        description="Pre-built AI tool workflows for Real Estate, Marketing, E-commerce, Accounting, Healthcare, and Hospitality. See exactly which tools work together to eliminate legacy pain points."
        slug="ai-stacks"
        author="Wowrolla Strategic"
      />

      {/* Hero + Selector */}
      <section
        className="relative pt-28 pb-6 px-6 overflow-hidden"
        style={{
          backgroundImage: selectedStack?.image
            ? `linear-gradient(110deg, ${isLightTheme ? 'rgba(246,248,251,0.96) 24%, rgba(246,248,251,0.78) 100%' : 'rgba(5,5,8,0.95) 42%, rgba(5,5,8,0.68) 100%'}, url('${selectedStack.image}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 1200 400" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#FFE800" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="1200" height="400" fill="url(#grid)" />
          </svg>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="container mx-auto max-w-7xl relative z-10"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-[#FFE800] font-mono mb-3">
                Strategic Workflows
              </p>
              <h1
                className={`text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-tight ${heroTitleClass}`}
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                AI Stack Prescriptions for SMBs & Professionals
              </h1>
              <p className={`text-base max-w-3xl ${bodyTextClass}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Stop shopping for individual AI tools. See exactly which tools work together, in what order, to eliminate
                legacy pain points. Each prescription includes workflow sequencing, cost range, and execution outcomes.
              </p>
              <p className="text-xs uppercase tracking-[0.16em] text-[#FFE800] font-mono mt-4">Select a business type from the canonical ribbon</p>
              <p className={`text-sm mt-2 ${isLightTheme ? 'text-[#475569]' : 'text-[#B7C1D8]'}`}>Active stack: <span className={`font-semibold ${isLightTheme ? 'text-[#0F172A]' : 'text-white'}`}>{selectedStack.title}</span> · {selectedStack.metric}</p>
            </div>
            <div className="w-full lg:w-[560px]">
              <div className={heroCardClass}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#FFE800] font-mono">Selected Business</p>
                <p className={`text-2xl font-black leading-tight mt-1 ${heroTitleClass}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {selectedStack.title}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
              <div className={heroStatCardClass}>
                <div className="text-[#FFE800] font-bold text-xl">{BUSINESS_TYPE_RIBBON_ITEMS.length}</div>
                <div className={`text-[10px] uppercase tracking-widest ${mutedTextClass}`}>Industries</div>
              </div>
              <div className={heroStatCardClass}>
                <div className="text-[#39FF14] font-bold text-xl">{minAnnualCost}-{maxAnnualCost}</div>
                <div className={`text-[10px] uppercase tracking-widest ${mutedTextClass}`}>Annual Cost</div>
              </div>
              <div className={heroStatCardClass}>
                <div className="text-[#00F0FF] font-bold text-xl">4-7</div>
                <div className={`text-[10px] uppercase tracking-widest ${mutedTextClass}`}>Tools/Stack</div>
              </div>
              </div>
            </div>
          </div>

          <div className={heroRibbonClass}>
            <motion.div
              className="flex gap-3 w-max"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 38, ease: 'linear', repeat: Infinity }}
            >
              {carouselStacks.map((industry, idx) => {
                const IconComponent = industry.icon;
                const isSelected = selectedBusinessType === industry.id;
                return (
                  <motion.button
                    key={`${industry.id}-${idx}`}
                    onClick={() => {
                      setStackActionMessage('');
                      setSelectedStack(industry.mappedStack);
                      setSelectedBusinessType(industry.id);
                      persistBusinessSelection(industry.mappedStack.id, industry.id);
                      localStorage.setItem('wowrolla:selected_stack', industry.mappedStack.id);
                    }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className={`min-w-[198px] p-3 rounded-none border transition-all duration-200 flex flex-col items-center gap-2 ${isSelected ? selectedIndustryClass : 'bg-transparent border-white/10 hover:border-white/20'}`}
                  >
                    <IconComponent size={20} className={isSelected ? (isLightTheme ? 'text-[#B67A00]' : 'text-[#FFE800]') : (isLightTheme ? 'text-[#334155]' : 'text-white')} />
                    <span className={`text-sm font-semibold text-center line-clamp-2 ${isLightTheme ? 'text-[#0F172A]' : 'text-white'}`}>{industry.title}</span>
                  </motion.button>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Main Stack Display */}
      <motion.section
        key={selectedStack.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto max-w-7xl px-6 py-12"
      >
        {/* Three-Column: Pain | Workflow | Outcome */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Pain Points */}
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="terminal-grid p-5 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle size={20} className="text-[#FF6B6B]" />
              <h3 className={`text-lg font-bold ${heroTitleClass}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Legacy Pain
              </h3>
            </div>
            <ul className="space-y-2">
              {selectedStack.painPoints.map((point, idx) => (
                <li key={idx} className={`text-base flex gap-2 leading-snug ${bodyTextClass}`}>
                  <span className="text-[#FFE800] font-bold flex-shrink-0">-</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Tool Workflow */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="terminal-grid p-5 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap size={20} className="text-[#FFE800]" />
              <h3 className={`text-lg font-bold ${heroTitleClass}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                AI Stack Workflow + Agent Layer
              </h3>
            </div>
            <div className="space-y-2">
              {workflowWithAgent.map((tool, idx) => (
                <div key={`${tool.name}-${idx}`} className={`border-l-2 pl-2.5 ${tool.isAgent ? 'border-[#39FF14]/60' : 'border-[#FFE800]/30'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`font-semibold text-base inline-flex items-center gap-2 leading-tight ${heroTitleClass}`}>
                        {tool.isAgent && <Bot size={14} className="text-[#39FF14]" />}
                        {tool.name}
                        {tool.isAgent && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#39FF14]/40 text-[#39FF14]">
                            Agent
                          </span>
                        )}
                      </p>
                      <p className={`text-sm ${tool.isAgent ? 'text-[#9CFB8F]' : (isLightTheme ? 'text-[#64748B]' : 'text-[#8A8A93]')} leading-snug`}>{tool.role}</p>
                    </div>
                    <span className="text-xs font-mono text-[#39FF14] ml-2 flex-shrink-0">Step {idx + 1}</span>
                  </div>
                  {idx < workflowWithAgent.length - 1 && (
                    <div className="text-[#39FF14] text-xs font-bold mt-1">-&gt;</div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Cost & Outcome */}
          <motion.div
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="terminal-grid p-5 border border-white/10"
          >
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={20} className="text-[#39FF14]" />
              <h3 className={`text-lg font-bold ${heroTitleClass}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                AI-First Outcome
              </h3>
            </div>
            <p className="text-base text-[#FFE800] font-semibold mb-3 leading-snug">{selectedStack.outcome}</p>

            <p className={`text-sm mb-3 ${bodyTextClass}`}>
              Implementation window: <span className={`font-semibold ${isLightTheme ? 'text-[#0F172A]' : 'text-white'}`}>{selectedStack.implementationWindow}</span>
            </p>

            <div className={darkInfoBoxClass}>
              <div className="flex items-baseline gap-2 mb-2">
                <DollarSign size={16} className="text-[#39FF14] flex-shrink-0" />
                <div>
                  <p className={`text-xs uppercase tracking-widest ${mutedTextClass}`}>Annual Cost</p>
                  <p className={`text-xl font-bold font-mono ${heroTitleClass}`}>{selectedStack.annualCost}</p>
                </div>
              </div>
              <p className={`text-xs mt-3 font-mono ${mutedTextClass}`}>{selectedStack.costBreakdown}</p>
            </div>
          </motion.div>
        </div>

        {/* Competitive Insight */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className={insightPanelClass}
        >
          <p className="text-xs uppercase tracking-[0.16em] text-[#00F0FF] font-mono mb-2">Workflow Mapping (Aligned With Readiness + ROI)</p>
          <p className={`text-sm mb-3 ${bodyTextClass}`}>Use this as a directional sequence; final matching and ROI-fit happens in Readiness & ROI based on your ecosystem, current stack, and complexity inputs.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {technicalWorkflow.map((stage) => (
              <div key={stage.stage} className={stageCardClass}>
                <p className={`text-sm font-bold mb-1 ${heroTitleClass}`}>{stage.stage}</p>
                <p className={`text-sm mb-2 leading-snug ${bodyTextClass}`}>{stage.objective}</p>
                <p className="text-xs uppercase tracking-wider text-[#7FC4FF] mb-1">Use-Case Tools</p>
                <p className={`text-sm mb-2 ${isLightTheme ? 'text-[#334155]' : 'text-[#DCEBFF]'}`}>{stage.aiTools.length ? stage.aiTools.join(', ') : 'None listed'}</p>
                <p className="text-xs uppercase tracking-wider text-[#D0B2FF] mb-1">System Integrations</p>
                <p className={`text-sm mb-2 ${isLightTheme ? 'text-[#475569]' : 'text-[#F0E8FF]'}`}>{stage.nonAiTools.length ? stage.nonAiTools.join(', ') : 'None listed'}</p>
                <p className="text-xs uppercase tracking-wider text-[#7EFCA8] mb-1">Agent Control Logic</p>
                <p className={`text-sm leading-snug ${isLightTheme ? 'text-[#166534]' : 'text-[#CCFFD9]'}`}>{stage.agentAction}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Competitive Insight */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className={whyPanelClass}
        >
          <p className="text-xs uppercase tracking-[0.16em] text-[#39FF14] font-mono mb-2">Why This Matters</p>
            <p className={`text-base leading-relaxed ${heroTitleClass}`}>{selectedStack.competitorNote}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="neon-info-section mb-8"
        >
          <p className="text-sm neon-yellow font-semibold mb-2">This may not be the exact final stack for your team.</p>
          <p className={`text-base leading-snug mb-4 ${heroTitleClass}`}>
            Use this as your launch point, then keep refining. You can add these tools now, switch to Find Your Tools, run Build Your Stack, and jump back to your dashboard at any time without breaking flow.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <button
              type="button"
              onClick={addSelectedToolsToMyStack}
              disabled={addingPreset}
              className="neon-btn"
            >
              {addingPreset ? 'Adding...' : 'Add These To My Tool Stack'}
            </button>
            <Link
              to={`/my-stack/build?from=ai-stacks&stack=${selectedStack.id}`}
              className="neon-btn-alt"
            >
              Build Your Stack
            </Link>
            <Link
              to={`/find?from=ai-stacks&stack=${selectedStack.id}`}
              className="neon-btn-alt"
            >
              Find Your Tools
            </Link>
            <Link
              to={`/dashboard/stack?from=ai-stacks&stack=${selectedStack.id}`}
              className="neon-btn-alt"
            >
              My Tool Dashboard
            </Link>
          </div>
          {stackActionMessage && <p className="text-sm text-[#A2FDBF] mt-3">{stackActionMessage}</p>}
        </motion.div>

        {/* Next Steps CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className="terminal-grid p-5 border border-white/10">
            <h4 className={`font-bold mb-2 text-lg ${heroTitleClass}`}>Ready to implement?</h4>
            <p className={`text-base mb-4 leading-snug ${bodyTextClass}`}>
              Get personalized setup guidance for your specific business size and workflow.
            </p>
            <Link
              to="/consulting"
              className="bg-[#FFE800] text-black font-semibold px-4 py-2 rounded-none hover:bg-[#FFF04D] transition-colors inline-flex items-center gap-2"
            >
              Book a 20-Min Consultation
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="terminal-grid p-5 border border-white/10">
            <h4 className={`font-bold mb-2 text-lg ${heroTitleClass}`}>Launch this stack now</h4>
            <p className={`text-base mb-4 leading-snug ${bodyTextClass}`}>
              Jump into Finder with this stack context and turn these workflow steps into a concrete tool setup.
            </p>
            <Link
              to={`/find?from=ai-stacks&stack=${selectedStack.id}`}
              className="bg-transparent border border-white/20 text-white font-semibold px-4 py-2 rounded-none hover:bg-white/5 transition-colors inline-flex items-center gap-2"
            >
              Open in Finder
              <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>
      </motion.section>

      {/* Comparison Table Section */}
      <section className={tableSectionClass}>
        <div className="container mx-auto max-w-7xl">
          <h2
            className={`text-3xl sm:text-4xl font-black mb-4 ${heroTitleClass}`}
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            All Stacks at a Glance
          </h2>
          <p className={`mb-8 ${bodyTextClass}`}>
            Compare annual costs, primary pain points solved, and ROI metrics across all prescriptions.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className={`text-left border-b border-white/10 font-mono uppercase tracking-widest text-xs ${mutedTextClass}`}>
                  <th className="py-4 px-4">Business Type</th>
                  <th className="py-4 px-4">Primary Pain</th>
                  <th className="py-4 px-4">Annual Cost</th>
                  <th className="py-4 px-4">Key Metric</th>
                  <th className="py-4 px-4">Tools Required</th>
                </tr>
              </thead>
              <tbody>
                {STACK_PRESCRIPTIONS.map((stack) => (
                  <tr
                    key={stack.id}
                    className={`border-b border-white/5 transition-colors ${
                      selectedStack.id === stack.id ? (isLightTheme ? 'bg-[#EAF2FA]' : 'bg-white/5') : (isLightTheme ? 'hover:bg-[#F1F5F9]' : 'hover:bg-white/[0.02]')
                    }`}
                  >
                    <td className="py-4 px-4">
                      <button
                        onClick={() => {
                          setStackActionMessage('');
                          setSelectedStack(stack);
                          setSelectedBusinessType(resolveBusinessTypeFromStackId(stack.id, DEFAULT_BUSINESS_TYPE));
                          localStorage.setItem('wowrolla:selected_stack', stack.id);
                        }}
                        className={`font-semibold transition-colors flex items-center gap-2 ${heroTitleClass} ${isLightTheme ? 'hover:text-[#B67A00]' : 'hover:text-[#FFE800]'}`}
                      >
                        {React.createElement(stack.icon, { size: 16 })}
                        {stack.title}
                      </button>
                    </td>
                    <td className={`py-4 px-4 ${bodyTextClass}`}>{stack.painPoints[0]}</td>
                    <td className="py-4 px-4 text-[#FFE800] font-semibold">{stack.annualCost}</td>
                    <td className="py-4 px-4 text-[#39FF14] font-mono text-xs">{stack.metric}</td>
                    <td className={`py-4 px-4 ${bodyTextClass}`}>{tableToolCount[stack.id]} tools (incl. agent)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Wowrolla FAQ Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2
            className={`text-3xl sm:text-4xl font-black mb-8 ${heroTitleClass}`}
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Why Wowrolla Over &quot;Tool List&quot; Sites?
          </h2>

          <div className="space-y-4">
            {whyWowrollaFaqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                className="terminal-grid border border-white/10"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full text-left p-5 flex items-center justify-between gap-4"
                >
                  <span className={`text-base sm:text-lg font-semibold ${heroTitleClass}`}>{faq.question}</span>
                  {openFaqIndex === index ? (
                    <Minus size={20} className="text-[#FFE800] flex-shrink-0" />
                  ) : (
                    <Plus size={20} className="text-[#FFE800] flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className={`px-5 pb-5 text-sm leading-relaxed ${bodyTextClass}`}>
                    {faq.answer}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next Steps / Lead Capture */}
      <section className={nextStepsSectionClass}>
        <div className="container mx-auto max-w-5xl">
          <h2
            className={`text-3xl sm:text-4xl font-black mb-6 text-center ${heroTitleClass}`}
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            What Comes Next
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} className="terminal-grid p-6 border border-white/10">
              <div className="text-2xl font-bold text-[#FFE800] mb-2">1.</div>
              <h3 className={`font-bold mb-2 ${heroTitleClass}`}>AI Stack Builder Quiz</h3>
              <p className={`text-sm ${bodyTextClass}`}>
                3 questions (industry, team size, biggest pain) {'->'} custom stack recommendation. Lead magnet.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="terminal-grid p-6 border border-white/10">
              <div className="text-2xl font-bold text-[#39FF14] mb-2">2.</div>
              <h3 className={`font-bold mb-2 ${heroTitleClass}`}>Stack Cost Calculator</h3>
              <p className={`text-sm ${bodyTextClass}`}>
                Annual savings vs. legacy SaaS they&apos;re replacing. Anchors your consulting rate.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="terminal-grid p-6 border border-white/10">
              <div className="text-2xl font-bold text-[#00F0FF] mb-2">3.</div>
              <h3 className={`font-bold mb-2 ${heroTitleClass}`}>Consulting Package</h3>
              <p className={`text-sm ${bodyTextClass}`}>
                &quot;AI Stack Audit&quot; for SMBs: $500-2K for stack assessment + implementation roadmap.
              </p>
            </motion.div>
          </div>

          <div className="terminal-grid p-8 border border-white/10 text-center">
            <p className={`mb-6 ${bodyTextClass}`}>
              These prescriptions are the hook. Consulting is the conversion. Every stack card leads to a discovery call.
            </p>
            <Link
              to="/consulting"
              className="bg-[#FFE800] text-black font-semibold px-8 py-3 rounded-none hover:bg-[#FFF04D] transition-colors inline-flex items-center gap-2"
            >
              <Users size={18} />
              Start Your Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIStackPrescriptionsPage;

